import { DelugeGame } from '../../games/deluge/DelugeGame.js';
import { DelugeVortex } from '../../games/deluge/DelugeVortex.js';
import { DelugeRenderer } from '../../games/deluge/DelugeRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Deluge-specific types ──────────────────────────────────────────

export interface DelugeRenderOptions {
    // Placeholder — DelugeRenderer is fully game-state driven
}

// ── Adapter ───────────────────────────────────────────────────────

export class DelugeTutorialAdapter implements TutorialGameAdapter {
    private game: DelugeGame;
    private renderer: DelugeRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new DelugeGame(grid);
        this.renderer = new DelugeRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        if (typeof state !== 'string') return;

        let serialized = state;
        let maxTime: number | null = null;

        // Parse @N suffix for simulate-until
        const atIndex = serialized.lastIndexOf('@');
        if (atIndex >= 0) {
            maxTime = parseFloat(serialized.substring(atIndex + 1));
            serialized = serialized.substring(0, atIndex);
        }

        this.game.reset();
        if (serialized) {
            this.game.deserialize(serialized);
        }

        if (maxTime !== null) {
            this.game.limitDisplayTime(maxTime);
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createDelugeTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new DelugeTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface DelugeVortexTutorialState {
    deluge: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the
     * adapter's default teleport pair for this scene only — used by the
     * activation scene to show a second, blocked (amber) pair. Omit otherwise.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}


// ── Vortex Adapter ───────────────────────────────────────────────

export class DelugeVortexTutorialAdapter implements TutorialGameAdapter {
    private game: DelugeVortex;
    private renderer: DelugeRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new DelugeVortex(grid);
        this.renderer = new DelugeRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as DelugeVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);
        if (s?.deluge) {
            this.game.deserialize(s.deluge);
        }
        this.currentGateMode = s?.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createDelugeVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex deluge tutorial: B3 ↔ C0
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 3, col: 1 }, { row: 0, col: 2 }],
    ];
    return new DelugeVortexTutorialAdapter(canvas, grid, gatePairs);
}
